import FbButton, { FbButtonVariants } from 'components/FbUI/FbButton'
import React from 'react'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { BsChevronDown } from 'react-icons/bs'
import { cn } from '../cn'
import {
  ds2026DropdownContentClass,
  ds2026DropdownItemClass,
  ds2026DropdownItemDestructiveClass,
  ds2026DropdownSeparatorClass,
} from 'components/FbUI/ds2026DropdownMenu'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '../BaseDropdown/dropdown-base'

/**
 * Lets a `subContent` element hide the parent dropdown while keeping its
 * React subtree mounted.
 *
 * Sub-menu items that open their own modal (e.g. "Push to Campaign ->
 * Add new") must visually dismiss the parent Radix menu when the modal
 * opens. Otherwise the menu lingers on top of the modal because Radix
 * keeps the dropdown open until a pointer/escape event it recognises,
 * and the modal's overlay swallows those events (FIR-7473).
 *
 * We cannot simply close the Radix root: that unmounts the sub-content,
 * destroying both the modal's open state and the post-submit logic that
 * lives in the sub-content component. Instead, `suppress()` hides the
 * menu (visually + non-interactive) without unmounting, and `restore()`
 * brings it back when the modal closes.
 */
interface DropdownSuppression {
  suppress: () => void
  restore: () => void
}

const DropdownSuppressionContext = React.createContext<DropdownSuppression>({
  suppress: () => {},
  restore: () => {},
})

/**
 * Hook for a `subContent` element to hide/show its parent dropdown without
 * unmounting. See {@link DropdownSuppression}. Returns no-op handlers when
 * the consumer is not rendered inside a {@link Dropdown}.
 */
export function useDropdownSuppression(): DropdownSuppression {
  return React.useContext(DropdownSuppressionContext)
}

/**
 * Classes that hide a menu surface while a sub-content modal owns the screen.
 *
 * Must be `display:none`, not `opacity-0 pointer-events-none`. Radix keeps the
 * menu `data-state="open"` and writes an inline `pointer-events: auto` plus
 * `z-index` on the popper content *and* its wrapper, so a Tailwind
 * `pointer-events-none` class loses to the inline style and the (invisible)
 * menu stays interactive on top of the modal: clicks land on hidden menu items
 * and a stray hover over a sibling item closes the open sub-menu, unmounting
 * the modal's subtree (FIR-7473 regression). `!hidden` removes the surface from
 * layout, hit-testing and the a11y tree while React keeps it mounted, so the
 * sub-content's modal state and post-submit logic survive. The `!` beats any
 * `display` utility on the surface; Radix sets no inline `display`.
 */
const SUPPRESSED_MENU_CLASSES = '!hidden'

export interface IDropdownItem {
  label: string | React.JSX.Element
  icon?: React.JSX.Element
  callback?: () => void
  disabled?: boolean
  disabledTooltipText?: string
  subContent?: React.JSX.Element
  hidden?: boolean
  isGroupHeader?: boolean
  /** Renders the item with the DS2026 destructive (red) treatment. */
  destructive?: boolean
  /** Draws a divider directly above this item to set it apart from the
   *  group above (e.g. separating a destructive Delete from normal actions). */
  separatorBefore?: boolean
}

export interface IDropdownProps {
  items: IDropdownItem[]
  buttonLabel?: string | React.JSX.Element
  buttonIcon?: React.JSX.Element
  buttonVariant?: FbButtonVariants
  menuLabel?: string
  disabled?: boolean
  className?: string
  /** Extra classes for the floating menu surface (DropdownMenuContent). */
  contentClassName?: string
  /** Extra classes applied to each DropdownMenuItem (e.g. hover override). */
  itemClassName?: string
  buttonComponent?: React.JSX.Element
  onOpenChange?: (open: boolean) => void
  /** Where the menu aligns relative to the trigger. Defaults to
   *  Radix's `center`. Use `end` when the trigger sits near the
   *  right edge so the menu opens flush under it instead of getting
   *  shifted left by collision detection. */
  align?: 'start' | 'center' | 'end'
}

export function Dropdown(props: IDropdownProps): React.JSX.Element {
  const [open, setOpen] = React.useState(false)
  // When a sub-content modal takes over, hide the menu without
  // unmounting it (see DropdownSuppressionContext).
  const [suppressed, setSuppressed] = React.useState(false)

  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      setOpen(next)
      // A fresh open always starts unsuppressed.
      if (next) setSuppressed(false)
      props.onOpenChange?.(next)
    },
    [props.onOpenChange]
  )

  const suppression = React.useMemo<DropdownSuppression>(
    () => ({
      suppress: () => setSuppressed(true),
      restore: () => setSuppressed(false),
    }),
    []
  )

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger disabled={props.disabled} asChild>
        {props.buttonComponent ?? (
          <FbButton className={props.className} variant={props.buttonVariant}>
            <div className={'flex items-center justify-between gap-2'}>
              <div className={'flex items-center gap-2'}>
                {props.buttonIcon}
                <span className={'hidden xl:inline'}>{props.buttonLabel}</span>
              </div>
              <div>
                <BsChevronDown />
              </div>
            </div>
          </FbButton>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className={cn(
          // DS2026 floating-menu surface (white card, hairline border, drop
          // shadow) so this menu matches the per-row ActionsDropdown menus.
          ds2026DropdownContentClass,
          props.contentClassName,
          // Keep mounted but fully hidden (display:none) while a sub-content
          // modal is open so the menu does not linger or intercept events on
          // top of it (FIR-7473). display:none also drops it from the a11y
          // tree; the modal renders in its own Dialog portal and stays
          // reachable.
          suppressed && SUPPRESSED_MENU_CLASSES
        )}
        align={props.align}
      >
        {props.menuLabel ? (
          <>
            <DropdownMenuLabel className="!bg-white !px-3 !pb-1 !pt-1.5 !text-sm !font-semibold !text-neutral-900 tabular-nums">
              {props.menuLabel}
            </DropdownMenuLabel>
            <DropdownMenuSeparator className={ds2026DropdownSeparatorClass} />
          </>
        ) : null}
        <DropdownMenuGroup>
          {props.items
            .filter((item) => !item.hidden)
            .map((item, idx) => {
              // Optional divider above this item (e.g. to fence off a
              // destructive Delete from the rest of the menu).
              const separator = item.separatorBefore ? (
                <DropdownMenuSeparator
                  className={ds2026DropdownSeparatorClass}
                />
              ) : null

              let node: React.JSX.Element

              if (item.isGroupHeader) {
                node = (
                  <div className="p-2 text-sm font-semibold text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-sm w-full my-1">
                    {item.label}
                  </div>
                )
              } else if (item.subContent && !item.disabled) {
                node = (
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger className={ds2026DropdownItemClass}>
                      {item.icon}
                      {item.label}
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent
                      className={cn(
                        ds2026DropdownContentClass,
                        suppressed && SUPPRESSED_MENU_CLASSES
                      )}
                    >
                      <DropdownSuppressionContext.Provider value={suppression}>
                        {item.subContent}
                      </DropdownSuppressionContext.Provider>
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                )
              } else {
                const menuItem = (
                  <DropdownMenuItem
                    className={cn(
                      // DS2026 item: gray hover, comfortable hit area — matches
                      // the per-row ActionsDropdown. Destructive items use the
                      // red treatment instead (never both — they conflict).
                      item.destructive
                        ? ds2026DropdownItemDestructiveClass
                        : ds2026DropdownItemClass,
                      props.itemClassName
                    )}
                    onClick={() => !item.disabled && item.callback?.()}
                    disabled={item.disabled}
                  >
                    {item.icon}
                    {item.label}
                  </DropdownMenuItem>
                )

                node =
                  item.disabled && item.disabledTooltipText ? (
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id={`tooltip-${item.label}`}>
                          {item.disabledTooltipText}
                        </Tooltip>
                      }
                    >
                      <div>{menuItem}</div>
                    </OverlayTrigger>
                  ) : (
                    menuItem
                  )
              }

              return (
                <React.Fragment key={idx}>
                  {separator}
                  {node}
                </React.Fragment>
              )
            })}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
